import React, { useEffect, useState } from "react";
import BlastTrace from "../../components/BlastTrace";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldCheck, AlertTriangle, Loader2, XCircle } from "lucide-react";
import api from "../../lib/api";
import { formatItalianDate } from "../../lib/dateUtils";
import { Logo } from "../../components/Logo";

// Pagina pubblica di tracciabilità — nessuna autenticazione, nessun dato personale.
// Due varianti, stesso formato dati (vedi routers_production.py):
// - QR pubblico/vendita (fixed=false): univoco per lotto, cambia a ogni produzione.
// - QR fisso/vetrina (fixed=true): fisso per ricetta+sede, sempre lo stesso,
//   risolve automaticamente all'ultima produzione (e ai relativi abbattimenti).
export default function TracePublic({ fixed = false }) {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    api.get(`/public/${fixed ? "trace-fisso" : "trace"}/${token}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [token, fixed]);

  return (
    <div className="min-h-screen bg-haccp-bg flex flex-col items-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center my-8"><Logo size="md" /></div>
        {data === undefined ? (
          <div className="flex justify-center py-16 text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : data === null ? (
          <div className="bg-white rounded-2xl shadow-card p-8 text-center" data-testid="trace-invalid">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold">QR non valido</h1>
            <p className="text-slate-500 font-body mt-2">Il codice scansionato non corrisponde ad alcuna preparazione.</p>
          </div>
        ) : data.nessuna_produzione ? (
          <div className="bg-white rounded-2xl shadow-card overflow-hidden" data-testid="trace-no-production">
            <div className="bg-navy text-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-bright">Tracciabilità preparazione</div>
              <h1 className="font-heading text-2xl font-bold mt-1">{data.denominazione}</h1>
            </div>
            <div className="p-6 text-center">
              <AlertTriangle className="w-9 h-9 text-amber-500 mx-auto mb-3" />
              <p className="font-body text-navy font-medium">Per questa ricetta non risulta nessuna produzione.</p>
              <p className="text-slate-500 font-body text-sm mt-2">La scheda si aggiornerà automaticamente alla prima produzione registrata.</p>
            </div>
          </div>
        ) : (
          (() => {
            const producedAt = data.data_produzione ? new Date(data.data_produzione) : null;
            const expiryDate = (producedAt && data.scadenza_giorni != null)
              ? new Date(producedAt.getTime() + data.scadenza_giorni * 86400000) : null;
            return (
          <div className="bg-white rounded-2xl shadow-card overflow-hidden animate-fade-up" data-testid="trace-public">
            <div className="bg-navy text-white p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-gold-bright">Tracciabilità preparazione</div>
              <h1 className="font-heading text-2xl font-bold mt-1">{data.denominazione}</h1>
              <div className="font-mono text-gold-bright mt-1">{data.lotto}</div>
              {fixed && <div className="mt-2 text-[11px] text-white/70">Scheda sempre aggiornata all'ultima produzione</div>}
            </div>
            <div className="p-6 space-y-4">
              {data.avviso_ritiro && (
                <div className="rounded-lg bg-red-600 p-4 text-white" role="alert" data-testid="trace-recall">
                  <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-5 w-5" /> {data.avviso_ritiro.tipo === "richiamo" ? "PRODOTTO RICHIAMATO" : "PRODOTTO RITIRATO DAL COMMERCIO"}</div>
                  <p className="mt-1 text-sm">{data.avviso_ritiro.tipo === "richiamo" ? "Non consumare questo prodotto e riportalo al punto vendita." : "Questo lotto non deve essere venduto né utilizzato."}</p>
                </div>
              )}
              <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-navy" data-testid="trace-subjected-to">
                <ShieldCheck className="h-4 w-4 shrink-0 text-gold" />
                Sottoposto a: {data.blast_chilling_records?.length > 0 ? "Produzione e abbattimento" : "Produzione"}
              </div>
              <BlastTrace records={data.blast_chilling_records} token={token} selectedId={params.get("abbattimento")} />
              {data.allergeni?.length > 0 && (
                <div className="rounded-lg bg-red-50 ring-1 ring-red-100 p-3">
                  <div className="flex items-center gap-2 text-red-700 font-semibold text-sm"><AlertTriangle className="w-4 h-4" /> Allergeni</div>
                  <div className="mt-1 text-sm text-red-700">{data.allergeni.join(", ")}</div>
                </div>
              )}
              {data.tracce?.length > 0 && (
                <div className="rounded-lg bg-amber-50 ring-1 ring-amber-100 p-3">
                  <div className="text-amber-700 font-semibold text-sm">Può contenere tracce di</div>
                  <div className="mt-1 text-sm text-amber-700">{data.tracce.join(", ")}</div>
                </div>
              )}
              <Row label="Azienda" value={data.azienda} />
              <Row label="Sede di produzione" value={data.sede} />
              <Row label="Data di produzione" value={formatItalianDate(data.data_produzione)} />
              {expiryDate && (
                <Row label="Da consumarsi entro" value={`${formatItalianDate(expiryDate)} (${data.scadenza_giorni} gg)`} />
              )}
              {data.conservazione?.trim() && <Row label="Conservazione" value={data.conservazione} />}
              {data.consumo?.trim() && <Row label="Consumo" value={data.consumo} />}
              {fixed && data.lotti_in_conservazione?.length > 0 && (
                <div className="pt-2" data-testid="trace-lots-in-storage">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-body mb-2">Lotti in conservazione ({data.lotti_in_conservazione.length})</div>
                  <div className="space-y-2">
                    {data.lotti_in_conservazione.map((lot) => (
                      <a key={lot.lotto} href={`/t/${lot.token}`} className="block rounded-lg bg-haccp-bg ring-1 ring-hairline p-3 hover:ring-gold" data-testid={`trace-lot-${lot.lotto}`}>
                        <div className="flex justify-between gap-3">
                          <span className="font-mono text-sm font-semibold text-navy">{lot.lotto}</span>
                          {lot.ultimo && <span className="shrink-0 rounded-full bg-navy px-2 py-0.5 text-[10px] font-semibold text-white">Ultima produzione</span>}
                        </div>
                        <div className="mt-1 text-xs text-slate-600">Prodotto il {formatItalianDate(lot.data_produzione)}{lot.scadenza ? ` · scadenza ${formatItalianDate(lot.scadenza)}` : ""}{lot.scaduto ? " · SCADUTO" : ""}</div>
                        <div className="mt-1 text-xs text-slate-600">{lot.abbattimenti.length
                          ? lot.abbattimenti.map((b) => `${b.processo}: ${b.esito === "conforme" ? "conforme" : b.esito === "in_attesa" ? "in attesa di conferma" : "non conforme"}`).join(" · ")
                          : "Nessun abbattimento registrato"}</div>
                        <div className="mt-1 text-xs font-medium text-sky-800 underline">Apri la tracciabilità del lotto</div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {data.ingredienti?.length > 0 && (
                <div className="pt-2" data-testid="trace-ingredienti">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-body mb-2">Ingredienti e lotti utilizzati</div>
                  <div className="space-y-2">
                    {data.ingredienti.map((it, i) => (
                      <div key={i} className="rounded-lg bg-haccp-bg ring-1 ring-hairline p-3" data-testid={`trace-ing-${i}`}>
                        <div className="flex justify-between gap-3">
                          <span className="text-navy font-medium text-sm">{it.ingrediente}</span>
                          <span className="font-mono text-gold text-xs shrink-0">{it.lotto}{it.auto && <span className="ml-1 text-slate-400">(auto)</span>}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 pb-6">
              <div className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="w-4 h-4 text-gold" /> Informazioni pubbliche autorizzate. Nessun dato personale.</div>
            </div>
          </div>
            );
          })()
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-hairline pb-2 last:border-0">
    <span className="text-xs uppercase tracking-wider text-slate-400 font-body">{label}</span>
    <span className="text-navy font-body text-sm text-right">{value}</span>
  </div>
);
